import React, { useState, useEffect } from "react";
import { Spinner, Button } from "@heroui/react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ChatBubbleOvalLeftIcon, HandThumbUpIcon, EyeIcon, MapPinIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import "./VideoDisplayHeroUI.css";
import CenteredHeroNav from "../components/CenteredHeroNav";
import { VIDEO_TITLE_MAX_LENGTH } from "./videoConstants";
import { generateVideoSlug } from "../utils/seoHelpers";

const getDisplayTitle = (title) => {
  const normalized = title?.trim() || "Untitled clip";
  if (normalized.length <= VIDEO_TITLE_MAX_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, VIDEO_TITLE_MAX_LENGTH - 1).trim()}…`;
};

const INITIAL_LOAD = 6; // Cap initial DOM to 6 cards for performance
const LOAD_MORE = 6; // Load 6 more on pagination

const VideoDisplay = () => {
  const { encodedStateName } = useParams();
  const decodedStateName = decodeURIComponent(encodedStateName);
  const [allVideos, setAllVideos] = useState([]);
  const [displayedVideos, setDisplayedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE || "http://localhost:8080";
        const res = await fetch(`${baseUrl}/api/videos`);
        if (!res.ok) {
          throw new Error("Error fetching videos");
        }
        const data = await res.json();

        // Filter videos for this state
        const filtered = (data || []).filter((video) => {
          return (
            video.state &&
            video.state.toLowerCase() === decodedStateName.toLowerCase()
          );
        });

        setAllVideos(filtered);
        // Load initial batch
        setDisplayedVideos(filtered.slice(0, INITIAL_LOAD));
        setHasMore(filtered.length > INITIAL_LOAD);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [decodedStateName]);

  const loadMore = () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    const currentLength = displayedVideos.length;
    const nextVideos = allVideos.slice(currentLength, currentLength + LOAD_MORE);

    setDisplayedVideos(prev => [...prev, ...nextVideos]);
    setHasMore(currentLength + LOAD_MORE < allVideos.length);
    setLoadingMore(false);
  };

  // Custom Empty State Component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-96 p-8">
      <div className="max-w-md p-8 text-center bg-white dark:bg-gray-900 rounded-2xl shadow-lg">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <span className="text-6xl">😢</span>
          </div>
          <div className="space-y-3 text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 text-center">
              No Videos Found
            </h3>
            <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed text-center">
              Videos for <span className="font-semibold text-indigo-600 dark:text-indigo-400">{decodedStateName}</span> do not exist yet
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // End of Results Component
  const EndOfResults = () => (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="text-center space-y-3">
        <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center shadow-sm">
          <span className="text-3xl">✨</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
          You've seen it all!
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          That's all the videos from {decodedStateName} for now. Check back later for more!
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col video-page-container">
      {/* Consistent navbar */}
      <CenteredHeroNav />

      {/* Page content */}
      <div className="flex-1 w-full">
        <div className="w-full min-h-screen">
          <div className="w-full px-6 sm:px-8 lg:px-12 py-12">
            {/* Back Button */}
            <div className="max-w-6xl mx-auto mb-4">
              <Button
                variant="light"
                startContent={<ArrowLeftIcon className="w-4 h-4" />}
                onPress={() => navigate('/locations')}
                className="text-foreground-600 hover:text-foreground"
              >
                Back to Locations
              </Button>
            </div>

            {/* State Header - Compact Design */}
            <div className="max-w-6xl mx-auto mb-8">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-100 via-white to-secondary-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 p-6 sm:p-8 shadow-lg border border-primary-200/30 dark:border-slate-700/50">
                {/* Background Pattern - Dots */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
                    backgroundSize: '32px 32px'
                  }}></div>
                </div>

                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/10 dark:bg-indigo-500/20 border border-primary-500/20 dark:border-indigo-500/30 mb-3">
                    <MapPinIcon className="w-4 h-4 text-primary-600 dark:text-indigo-400" />
                    <span className="text-xs font-semibold text-primary-700 dark:text-indigo-300">Location</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-black mb-2 text-gray-900 dark:text-slate-50">
                    {decodedStateName}
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-slate-300 max-w-2xl">
                    Experience the open roads and vehicle stories that make {decodedStateName} unique
                  </p>
                </div>
              </div>
            </div>

            {loading ? (
              // Loading spinner with static container
              <div className="w-full flex justify-center items-center h-96">
                <div className="flex flex-col items-center gap-6 p-8 rounded-2xl bg-white dark:bg-gray-900 shadow-lg">
                  <Spinner size="lg" color="primary" />
                  <div className="text-center space-y-2">
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Loading videos...</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Discovering EV experiences</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full">
                {allVideos.length === 0 ? (
                  <EmptyState />
                ) : (
                  <>
                    {/* Video Grid - Event delegation for performance */}
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12"
                        onClick={(e) => {
                          // Event delegation - single listener for all cards
                          const card = e.target.closest('[data-video-id]');
                          if (card) {
                            const videoId = card.dataset.videoId;
                            const title = card.dataset.title;
                            const location = card.dataset.location;

                            // Generate SEO-friendly slug
                            const slug = generateVideoSlug(title, location, videoId);

                            // Navigate to slug-based URL
                            navigate(`/clips/${slug}`);
                          }
                        }}
                      >
                      {displayedVideos.map((video, index) => {
                        const cityLabel = video.location || video.city || decodedStateName;
                        return (
                          <div
                            key={video.id}
                            className="video-card-static"
                            data-video-id={video.id}
                            data-title={video.title || 'Untitled Video'}
                            data-location={cityLabel}
                          >
                            <div className="px-0 pb-2">
                              <div className="relative">
                                <span className="video-card-city-name">{cityLabel}</span>
                              </div>
                              <div className="video-card-media">
                                {video.status === "processing" ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 rounded-lg">
                                    <Spinner size="lg" color="primary" />
                                    <p className="mt-4 text-sm text-gray-400">Processing video...</p>
                                  </div>
                                ) : video.status === "error" ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center bg-red-900 rounded-lg">
                                    <p className="text-red-400 font-semibold">Processing failed</p>
                                    <p className="mt-2 text-sm text-gray-400">Please try uploading again</p>
                                  </div>
                                ) : (
                                  // Just show thumbnail - Mux player loads on video page
                                  <div
                                    className="w-full h-full bg-black rounded-lg relative overflow-hidden"
                                    style={{
                                      backgroundImage: video.PosterUrl ? `url(${video.PosterUrl})` : 'none',
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}
                                  >
                                    {/* Play overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
                                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                                        <svg className="w-8 h-8 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M8 5v14l11-7z"/>
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="video-card-title-container">
                                <h3 className="video-card-title-overlap line-clamp-2">{getDisplayTitle(video.title)}</h3>
                              </div>
                            </div>

                            <div className="video-card-footer-static">
                              <div className="video-card-meta">
                                <HandThumbUpIcon className="video-card-stat-icon video-card-like-icon" />
                                <span className="video-card-stat-count">{video.likeCount || 0}</span>
                                <span className="video-card-bullet" aria-hidden="true">•</span>
                                <ChatBubbleOvalLeftIcon className="video-card-stat-icon video-card-comment-icon" />
                                <span className="video-card-stat-count">{video.commentCount || 0}</span>
                                <span className="video-card-bullet" aria-hidden="true">•</span>
                                <EyeIcon className="video-card-stat-icon" />
                                <span className="video-card-stat-count">{video.views || 0}</span>
                                <span className="video-card-bullet" aria-hidden="true">•</span>
                                <span>{moment(video.createdAt * 1000).fromNow()}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>

                    {/* Load More Button - replaces intersection observer */}
                    {hasMore && !loadingMore && (
                      <div className="flex justify-center py-12">
                        <Button
                          size="lg"
                          color="primary"
                          variant="flat"
                          onPress={loadMore}
                          className="min-w-[200px]"
                        >
                          Load More Videos
                        </Button>
                      </div>
                    )}

                    {/* Loading More Indicator */}
                    {loadingMore && (
                      <div className="flex justify-center items-center py-12">
                        <div className="flex flex-col items-center gap-4">
                          <Spinner size="md" color="primary" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Loading more videos...</p>
                        </div>
                      </div>
                    )}

                    {/* End of Results */}
                    {!hasMore && displayedVideos.length > 0 && <EndOfResults />}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoDisplay;
